import React from 'react';
import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import laboklinTheme, { LABOKLIN_PAGE_BG } from '../theme/laboklinTheme';
import LaboklinChrome from '../components/mylab/LaboklinChrome';
import SubmissionFormView from '../components/mylab/SubmissionFormView';
import { LabBasketProvider } from '../contexts/LabBasketContext';
import ReportsListView from '../components/mylab2/ReportsListView';
import ReportWorkupView from '../components/mylab2/ReportWorkupView';

/**
 * MyLab with the workup on the page instead of in a chat drawer.
 *
 * The workflow is the one /mylab already runs - same conversation, same tools, same
 * diagnostic case, same engine, same turns. This route only presents it differently:
 * opening a report starts the interpretation and the panels fill in as it lands,
 * rather than the veterinarian pressing a button and reading a transcript.
 *
 * Kept alongside /mylab rather than replacing it, so the two can be shown against
 * each other. Nothing under components/mylab is modified by this route; the chrome
 * and the submission form are reused as they are.
 */

interface MyLab2PageProps {
  signOut: () => void;
}

const TITLES: Array<[RegExp, string]> = [
  [/^\/mylab2\/reports\/(.+)$/, 'Report'],
  [/^\/mylab2\/order\/new$/, 'Online order'],
  [/^\/mylab2\/?$/, 'Reports'],
];

export const MyLab2Page: React.FC<MyLab2PageProps> = ({ signOut }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const title = TITLES.find(([pattern]) => pattern.test(location.pathname))?.[1] ?? 'MyLab';

  return (
    <ThemeProvider theme={laboklinTheme}>
      <CssBaseline />
      {/* The app shell paints a photographic background with a transparent body, so
          this view has to lay its own opaque surface over it. */}
      <Box sx={{ minHeight: '100vh', bgcolor: LABOKLIN_PAGE_BG, position: 'relative', zIndex: 0 }}>
        {/* The basket is per-visit and in memory. It holds what was picked up while
            reading, and places nothing. */}
        <LabBasketProvider>
          <Box sx={{ minWidth: 0 }}>
            <LaboklinChrome title={title} userLabel="Niklas Närhinen (136244)" onSignOut={signOut}>
              <Routes>
                <Route path="/" element={<ReportsListView />} />
                <Route path="reports/:reportNo" element={<ReportWorkupView />} />
                <Route
                  path="order/new"
                  element={
                    <SubmissionFormView onAskWhichTests={() => navigate('/mylab2')} />
                  }
                />
                <Route path="*" element={<Navigate to="/mylab2" replace />} />
              </Routes>
            </LaboklinChrome>
          </Box>
        </LabBasketProvider>
      </Box>
    </ThemeProvider>
  );
};

export default MyLab2Page;
